import {ArrowTopRightOnSquareIcon} from "@heroicons/react/20/solid";
import {AnchorHTMLAttributes, forwardRef} from "react";

/** The standard inline link: brand-colored and underlined, from theme tokens so it follows light/dark. */
export const otsLinkClassName = [
  "font-medium text-[var(--ots-primary)] underline decoration-[var(--ots-outline)] underline-offset-4 transition-colors",
  "hover:decoration-[var(--ots-primary)]",
  "focus-visible:rounded-sm focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--ots-primary)]"
].join(" ");

/** Web addresses leave the app; tel:, mailto:, and relative links do not. */
export const isExternalHref = (href?: string): boolean => Boolean(href && /^https?:\/\//i.test(href));

export type OtsLinkProps = AnchorHTMLAttributes<HTMLAnchorElement> & {
  href: string;
  /**
   * Open in a new tab, marked with an arrow and a spoken "(opens in a new tab)". Defaults to true for
   * web addresses; phone and email links stay in place.
   */
  newTab?: boolean;
};

/** An inline link that says when it will open somewhere else. */
export const OtsLink = forwardRef<HTMLAnchorElement, OtsLinkProps>(function OtsLink({
  href,
  newTab,
  className,
  children,
  ...props
}, ref) {
  const opensNewTab = newTab ?? isExternalHref(href);

  return (
    <a
      ref={ref}
      href={href}
      target={opensNewTab ? "_blank" : undefined}
      rel={opensNewTab ? "noopener noreferrer" : undefined}
      className={[otsLinkClassName, className].filter(Boolean).join(" ")}
      {...props}>
      {children}
      {opensNewTab && (
        <>
          <ArrowTopRightOnSquareIcon aria-hidden="true" className="ml-1 inline size-3.5 align-[-0.125em]"/>
          <span className="sr-only"> (opens in a new tab)</span>
        </>
      )}
    </a>
  );
});
